import { useEffect, useState } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "../components/ui/sheet";
import { Loader2, TrendingUp, AlertTriangle, Sparkles, X } from "lucide-react";
import { deepDive } from "../lib/api";
import { motion } from "framer-motion";

const VERDICT_COLOR = { Buy: "#4ADE80", Hold: "#FBBF24", Watch: "#60A5FA" };

export default function DeepDivePanel({ open, onOpenChange, ticker, ipoName }) {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    if (!open || !ticker) return;
    setLoading(true); setError(null); setData(null);
    deepDive({ ticker: ticker.ticker, company: ticker.company, ipo_name: ipoName })
      .then((d) => { if (!cancelled) setData(d); })
      .catch((e) => { if (!cancelled) setError(e?.response?.data?.detail || e.message); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [open, ticker, ipoName]);

  const v = data?.verdict;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="deep-panel w-full overflow-y-auto border-l border-white/10 bg-[#09090B] text-white sm:max-w-[480px]"
        data-testid="deep-dive-panel"
      >
        <SheetHeader className="space-y-1 text-left">
          <div className="font-mono-jb text-[11px] uppercase tracking-[0.2em] text-zinc-500">
            Deep Dive
          </div>
          <SheetTitle className="font-chivo text-3xl font-black tracking-tighter text-white">
            ${ticker?.ticker}
            <span className="ml-2 text-base font-medium text-zinc-400">{data?.company || ticker?.company || ""}</span>
          </SheetTitle>
        </SheetHeader>

        {loading && (
          <div className="mt-12 flex flex-col items-center gap-3 text-zinc-400" data-testid="deep-dive-loading">
            <Loader2 className="h-6 w-6 animate-spin" />
            <span className="font-mono-jb text-xs uppercase tracking-widest">Analyzing fundamentals…</span>
          </div>
        )}

        {error && (
          <div className="mt-10 rounded-lg border border-red-500/30 bg-red-500/10 p-4 text-sm text-red-300" data-testid="deep-dive-error">
            {error}
          </div>
        )}

        {data && !loading && (
          <div className="mt-8 space-y-7">
            {/* Thesis */}
            <div>
              <div className="font-mono-jb text-[11px] uppercase tracking-[0.18em] text-zinc-500">Proxy Thesis</div>
              <p className="mt-2 font-outfit text-lg italic leading-snug text-zinc-200">
                “{data.proxy_thesis}”
              </p>
            </div>

            {/* Fundamentals bento */}
            <div className="grid grid-cols-3 gap-3" data-testid="deep-dive-fundamentals">
              <StatTile label="Market Cap" value={data.fundamentals?.market_cap} />
              <StatTile label="P/E or P/S" value={data.fundamentals?.pe_or_ps} />
              <StatTile label="Rev Growth" value={data.fundamentals?.revenue_growth} />
            </div>

            {/* Catalysts & Risks */}
            <div className="grid grid-cols-1 gap-4">
              <ListBlock
                icon={<TrendingUp className="h-4 w-4 text-emerald-400" />}
                title="Top 3 Catalysts"
                items={data.catalysts}
                color="#4ADE80"
                testid="deep-dive-catalysts"
              />
              <ListBlock
                icon={<AlertTriangle className="h-4 w-4 text-red-400" />}
                title="Top 3 Risks"
                items={data.risks}
                color="#F87171"
                testid="deep-dive-risks"
              />
            </div>

            {/* AI Verdict */}
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              className={`verdict-glow-${v} rounded-2xl border border-white/10 bg-[#0c0c0e] p-5`}
              data-testid="deep-dive-verdict"
            >
              <div className="flex items-center gap-2">
                <Sparkles className="h-4 w-4" style={{ color: VERDICT_COLOR[v] }} />
                <span className="font-mono-jb text-[11px] uppercase tracking-[0.18em] text-zinc-500">AI Verdict</span>
              </div>
              <div
                className="mt-2 font-chivo text-4xl font-black tracking-tighter"
                style={{ color: VERDICT_COLOR[v] }}
              >
                {v?.toUpperCase()}
              </div>
              <p className="mt-2 text-sm leading-relaxed text-zinc-300">{data.verdict_reason}</p>
            </motion.div>
          </div>
        )}

        <button
          onClick={() => onOpenChange(false)}
          className="absolute right-4 top-4 rounded-full p-2 text-zinc-500 hover:bg-white/5 hover:text-white"
          data-testid="deep-dive-close"
        >
          <X className="h-4 w-4" />
        </button>
      </SheetContent>
    </Sheet>
  );
}

function StatTile({ label, value }) {
  return (
    <div className="rounded-xl border border-white/10 bg-white/[0.02] p-3">
      <div className="font-mono-jb text-[10px] uppercase tracking-[0.18em] text-zinc-500">{label}</div>
      <div className="mt-1 font-mono-jb text-sm font-bold text-white">{value || "—"}</div>
    </div>
  );
}

function ListBlock({ icon, title, items, color, testid }) {
  return (
    <div className="rounded-xl border border-white/10 bg-white/[0.02] p-4" data-testid={testid}>
      <div className="flex items-center gap-2">
        {icon}
        <span className="font-mono-jb text-[11px] uppercase tracking-[0.18em] text-zinc-400">{title}</span>
      </div>
      <ul className="mt-3 space-y-2">
        {(items || []).map((it, i) => (
          <li key={i} className="flex gap-3 text-sm leading-relaxed text-zinc-200">
            <span className="mt-1 h-1.5 w-1.5 shrink-0 rounded-full" style={{ background: color }} />
            <span>{it}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}
